import {Injectable} from '@angular/core';
import {EventsHttpService} from '@api/http/events/events-http.service';
import {MatchService} from '../match/match.service';
import {IPicture} from '@api/models/image/picture';
import {IMatchTickerItem} from '../../models/match-ticker-item';
import {TickerItemType} from '@api/models/ticker/ticker-item-type';
import {TickerItemCategory} from '@api/models/ticker/ticker-item-category';
import {TickerItemPhase} from '@api/models/ticker/ticker-item-phase';

export interface IHeaderTickerItem {
  home: boolean;
  minute: string;
  playerName: string;
  type: TickerItemType;
}

@Injectable({
  providedIn: 'root'
})

export class MatchTickerService {

  public tickerUrl: string;
  public items: IMatchTickerItem[] = [];
  public goalHeaderItems: IHeaderTickerItem[] = [];
  public redCardHeaderItems: IHeaderTickerItem[] = [];
  public questions: IMatchTickerItem[] = [];
  public phaseMinutes = {
    regular: {
      halftime: 45,
      pause: 15,
    },
    overtime: {
      halftime: 15,
      pause: 5,
    }
  };

  constructor( private eventsHttpService: EventsHttpService,
               private matchService: MatchService, ) { }

  public async init(): Promise<void> {
    this.tickerUrl = this.matchService.tickerUrl;
    await this.loadItems();
  }

  public async loadItems(): Promise<void> {
    const eventId = this.matchService.match._id;
    const teamId = this.matchService.selectedTeamId;
    const items = await this.eventsHttpService.getTimeline(eventId, teamId) as IMatchTickerItem[];
    this.updateItems(items)
  }

  public getTeamPictures(teamId: string): IPicture[] {
    const teams = this.matchService.teams;
    if (!teams) {
      return [];
    }
    return teams.home?.teamid === teamId ? teams.home.pictures : teams.away.pictures;
  }

  public updateItems(items: IMatchTickerItem[]): void {
    const activitiesAndPosts: IMatchTickerItem[] = [];
    const questions: IMatchTickerItem[] = [];
    items.forEach((item) => {
      // Do not add unverified items
      if (!this.canAddItem(item)) {
        return;
      }
      if (item.category === TickerItemCategory.Question) {
        questions.push(item);
        return;
      }
      activitiesAndPosts.push(item);
    });
    this.items = activitiesAndPosts;
    this.setGoalItems(activitiesAndPosts);
    this.questions = questions;
    this.sortItems();
  }

  private setGoalItems(items: IMatchTickerItem[]): void {
    const teams = this.matchService.teams;
    if (!teams?.home?.teamid || !teams?.away?.teamid) {
      this.goalHeaderItems = [];
      this.redCardHeaderItems = [];
      return;
    }
    const goalTypes = [TickerItemType.Goal, TickerItemType.OwnGoal, TickerItemType.PenaltyGoal];
    const redCardTypes = [TickerItemType.RedCard, TickerItemType.YellowRedCard];
    this.goalHeaderItems = this.getHeaderTickerItems(items, goalTypes);
    this.redCardHeaderItems = this.getHeaderTickerItems(items, redCardTypes);
  }

  private getHeaderTickerItems(items: IMatchTickerItem[], filterTypes: TickerItemType[]): IHeaderTickerItem[] {
    const filteredItems = items.filter(item => filterTypes.includes(item.type));
    return filteredItems
      .map(item => this.getHeaderTickerItem(item))
      .filter(goalItem => goalItem !== null);
  }

  private getHeaderTickerItem(item: IMatchTickerItem): IHeaderTickerItem | null {
    const player = this.matchService.getLineupPlayerById(item.playerid);
    if (!player) {
      return null;
    }
    return {
      home: item.teamid === this.matchService.teams?.home?.teamid,
      minute: item.display_minute,
      playerName: player.playername || player.lastname,
      type: item.type,
    };
  }

  private addTickerItem(item: IMatchTickerItem): void {
    if (this.items.findIndex((i) => i._id === item._id) > -1) {
      this.updateTickerItem(item);
      return;
    }
    // Do not add unverified items
    if (!this.canAddItem(item)) {
      return;
    }
    item.needsUpdate = true;
    this.items.push(item);
    this.sortItems();
  }

  private async updateTickerItem(item: IMatchTickerItem): Promise<void> {
    const existing = this.items.find((i) => i._id === item._id);
    if (!existing) {
      this.addTickerItem(item);
      return;
    }
    item.needsUpdate = true;
    Object.assign(existing, item);
    this.sortItems();
  }

  private removeTickerItem(itemId: string): void {
    const index = this.items.findIndex((i) => i._id === itemId);
    if (index > -1) {
      this.items.splice(index, 1);
    }
  }

  private canAddItem(item: IMatchTickerItem): boolean {
    if (!this.matchService.match.sportradar?.sr_created) {
      return true;
    }
    return item.verified || item.sportradar?.sr_verified;
  }

  private sortItems(): void {
    this.items = this.items.sort((a, b) => this.sort(b, a));
  }

  /**
   * Sorting func taken from backend
   * For further info search under htdocs/api/routes/event.js at fulltimeline route
   */
  private sort(a: IMatchTickerItem, b: IMatchTickerItem): number {
    const idxA = Object.values(TickerItemPhase).findIndex(
      (phase) => phase === a.activation_relative_to
    );
    const idxB = Object.values(TickerItemPhase).findIndex(
      (phase) => phase === b.activation_relative_to
    );
    if (
      a.activation_milliseconds === b.activation_milliseconds &&
      a.activation === b.activation
    ) {
      if (a.type === TickerItemType.ThankYou) {
        return 1;
      }
      if (b.type === TickerItemType.ThankYou) {
        return -1;
      }
    }
    return (
      idxA - idxB ||
      a.activation_milliseconds - b.activation_milliseconds ||
      a._created - b._created
    );
  }
}
